import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import color from "../../components/utils/Colors";
import { useNavigate } from "react-router-dom";
import { ArrowForward, ChevronLeft } from "@mui/icons-material";
import CourseCardCart from "../../components/cards/CourseCardCart";
import {
  deleteDataFromTable,
  getDataFromTable,
  createPayment,
  enrollInCourse,
  createNotification,
} from "../../services/services";
import { useUser } from "../../services/Context/UserContext";
import { useSnackbar } from "../../components/utils/SnackBar";

const Cart: React.FC = () => {
  const { user } = useUser();
  const showSnackbar = useSnackbar();
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [reload, setReload] = useState(false);
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery("(max-width:900px)");

  useEffect(() => {
    if (user?.id) {
      getDataFromTable("userCart", { userId: user.id })
        .then(async (res: any) => {
          const cart = res?.data?.data || [];

          const cartWithCourse = await Promise.all(
            cart.map(async (item: any) => {
              const courseRes = await getDataFromTable("course", {
                id: item.productId,
              });

              return {
                ...item,
                course: courseRes?.data?.data?.[0],
              };
            }),
          );

          setCartItems(cartWithCourse);
        })
        .catch((err) => {
          console.error("Failed to fetch cart", err);
        });
    }
  }, [user?.id, reload]);

  const handleRemove = (id: number) => {
    deleteDataFromTable("userCart", id)
      .then(() => {
        setReload((prev) => !prev);
      })
      .catch((err) => {
        console.error("Failed to remove item", err);
      });
  };

  const totalPrice = cartItems.reduce((sum: number, item: any) => {
    return sum + (item?.course?.price ?? 0);
  }, 0);

  const handleProceedToPay = async () => {
    if (!user?.id || cartItems.length === 0) return;
    setProcessing(true);
    try {
      // Create payment and enroll for each item in cart
      await Promise.all(
        cartItems.map(async (item) => {
          if (item.course?.id) {
            const coursePrice = item.course.price || 0;
            const currency =
              item.course.unitOfPrice || item.course.currency || "USD";
            const generatedOrderId = `ORD-${Math.floor(Math.random() * 1000000)}`;
            const generatedTransactionId = `TXN-${Math.floor(Math.random() * 1000000)}`;

            await createPayment({
              amount: coursePrice,
              baseAmount: coursePrice,
              currency: currency,
              userId: user.id,
              modeOfPayment: "Online",
              status: "success",
              categoryOfPayment: "Course Enrollment",
              purpose: `Enrollment for ${item.course.title || "Course"}`,
              vatPercentage: 0,
              courseId: item.course.id,
              orderId: generatedOrderId,
              transactionId: generatedTransactionId,
              customerId: `CUST-${user.id}`,
              checkoutUrl: "https://example.com/checkout",
              paymentThirdPartyType: "Stripe",
              refundDate: new Date(
                Date.now() + 30 * 24 * 60 * 60 * 1000,
              ).toISOString(), // 30 days later
            });

            await enrollInCourse({ courseId: item.course.id, userId: user.id });
            await createNotification({
              title: "Course Purchased",
              message: `You have successfully enrolled in ${item.course.title || "Course"}.`,
              type: "Enrollment",
              userId: user.id,
            });
          }
          await deleteDataFromTable("userCart", item.id);
        }),
      );

      showSnackbar("Payment successful! You are now enrolled.");
      setCartItems([]);
      setReload((prev) => !prev);
    } catch (error: any) {
      console.error("Checkout failed", error);
      showSnackbar("Checkout failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleGoBack = () => navigate(-1);
  return (
    <div style={{ minHeight: "90vh", paddingBottom: "40px" }}>
      <Typography
        onClick={handleGoBack}
        sx={{
          margin: 0,
          position: "absolute",
          top: isSmallScreen ? "120px" : "150px",
          color: color.firstColor,
          fontSize: "16px",
          fontWeight: "bold",
          paddingLeft: isSmallScreen ? 5 : 10,
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <ChevronLeft /> Back
      </Typography>

      <Typography
        variant="h5"
        align="center"
        sx={{
          background: color.textColor,
          textAlign: "center",
          color: "white",
          width: "fit-content",
          margin: "auto",
          padding: "4px 16px",
          borderRadius: "4px",
          mb: 4,
          mt: 4,
        }}
      >
        Cart
      </Typography>

      {cartItems.length === 0 && (
        <Typography
          sx={{ textAlign: "center", color: "gray", mt: 10, fontSize: "18px" }}
        >
          Your cart is empty.
        </Typography>
      )}

      {cartItems.map((item: any, index: number) => (
        <Box
          key={item.id ?? index}
          p={4}
          pl={6}
          pr={6}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CourseCardCart
            courseName={
              item?.course?.title || item?.course?.courseName || "Course"
            }
            teachers={item?.course?.teachersName}
            localRating={item?.course?.localRating || 0}
            globalRating={item?.course?.globalRating || 0}
            price={item?.course?.price || 0}
            level={item?.course?.level || "Beginner"}
            duration={item?.course?.timeline || "N/A"}
            image={
              item?.course?.image ||
              item?.course?.coverImage ||
              "/assets/Course 1.png"
            }
            onRemove={() => handleRemove(item.id)}
            courseId={item?.course?.id}
            unitOfPrice={item?.course?.unitOfPrice}
          />
        </Box>
      ))}

      {cartItems.length > 0 && (
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexShrink: 0,
            borderRadius: "999px",
            bgcolor:
              theme.palette.mode === "light"
                ? "rgba(255, 255, 255, 0.4)"
                : "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(20px)",
            maxHeight: isSmallScreen ? 60 : "auto",
            p: isSmallScreen ? 1 : 2,
            width: "fit-content",
            height: "100px",
            position: "sticky",
            bottom: 5,
            ml: "auto",
            border: "solid 2px",
            borderColor: color.borderColor,
          })}
        >
          <Typography
            sx={{
              color: color.textColor,
              fontWeight: "bold",
              width: "fit-content",
              padding: "4px 16px",
              borderRadius: "4px",
              fontSize: isSmallScreen ? "16px" : "22px",
            }}
          >
            Total Price :{" "}
            {cartItems[0]?.course?.unitOfPrice === "INR" ? "₹" : "$"}
            {totalPrice}
          </Typography>

          <Button
            onClick={handleProceedToPay}
            disabled={processing}
            style={{
              background: color.firstColor,
              color: "white",
              borderRadius: "40px",
              textTransform: "none",
              transition: "transform 0.3s ease",
              padding: "10px 20px",
              fontSize: isSmallScreen ? "12px" : "18px",
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLButtonElement).style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLButtonElement).style.transform = "scale(1)";
            }}
          >
            {processing ? (
              <CircularProgress size={24} sx={{ color: "white", mx: 4 }} />
            ) : (
              <>
                Proceed to Pay <ArrowForward sx={{ ml: 2 }} />
              </>
            )}
          </Button>
        </Box>
      )}
    </div>
  );
};

export default Cart;
